import type { ButtonHTMLAttributes } from 'react';
import { Button } from '@/shared/components/Button';
import {
  RedPawn,
  BluePawn,
  YellowPawn,
  GreenPawn
} from '@/features/game/components/board/pawns/basics';
import { createResponsiveTextStroke } from '@/styles/textStroke';

export interface SkinsButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  /** Size class for the button (e.g., 'w-48 h-52') */
  className?: string;
  /** Show a "NEW" badge on the button */
  showNewBadge?: boolean;
}

export function SkinsButton({ className = 'w-48 h-52', showNewBadge, ...props }: SkinsButtonProps) {
  return (
    <Button variant="blue" className={className} {...props}>
      <div className="relative h-full w-full p-[6%]">
        {/* Pawns arrangement - horizontal row with overlap */}
        <div className="relative w-full h-[65%]">
          {/* Green pawn - far left */}
          <div className="absolute left-[0%] top-[5%] w-[45%] z-10">
            <GreenPawn className="w-full h-auto" />
          </div>
          {/* Red pawn - overlapping green */}
          <div className="absolute left-[20%] top-[0%] w-[45%] z-20">
            <RedPawn className="w-full h-auto" />
          </div>
          {/* Blue pawn - center */}
          <div className="absolute left-[40%] top-[3%] w-[45%] z-30">
            <BluePawn className="w-full h-auto" />
          </div>
          {/* Yellow pawn - far right */}
          <div className="absolute right-[0%] top-[0%] w-[45%] z-40">
            <YellowPawn className="w-full h-auto" />
          </div>
        </div>
        {/* SKINS text with Mind Boggle font and black stroke */}
        <span
          className="absolute bottom-[12%] left-1/2 -translate-x-1/2 font-mind-boggle text-[#F2F2F2] leading-none"
          style={{
            fontSize: 'clamp(1.25rem, 28cqw, 4rem)',
            fontWeight: 1000,
            ...createResponsiveTextStroke(1, 0.08),
            textShadow: `
              0.05em 0.05em 0 #000,
              -0.05em 0.05em 0 #000,
              0.05em -0.05em 0 #000,
              -0.05em -0.05em 0 #000,
              0.05em 0 0 #000,
              -0.05em 0 0 #000,
              0 0.05em 0 #000,
              0 -0.05em 0 #000
            `
          }}
        >
          SKINS
        </span>
        {/* NEW badge */}
        {showNewBadge && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none z-50 animate-pulse">
            NEW
          </span>
        )}
      </div>
    </Button>
  );
}

export default SkinsButton;
